import { MessagingPlatform } from '@this+that/models';

import { OnboardingStep } from '../../pages/private/Onboarding/Onboarding.constants';
import { APP_PAGES } from '../../routes/constants';

export const OAUTH_ERROR_PARAMS = {
  ERROR: 'error',
  ERROR_DESCRIPTION: 'error_description',
  ERROR_URI: 'error_uri',
  ERROR_REASON: 'error_reason',
} as const;

export const OAUTH_SUCCESS_PARAM = 'code';
export const OAUTH_STATE_PARAM = 'state';

export const OAUTH_PROVIDER_PARAMS: ReadonlyArray<string> = [
  'integration',
  'calendar',
  'provider',
];

export const UNKNOWN_OAUTH_PROVIDER = 'unknown';

// Outlook is the one provider whose redirect URIs carry no identifying query param
// (bare `/inbox`, bare `/onboarding/email-sync` — Google always adds its own), so
// the path is the only signal. Without this, refusals from the provider that
// motivated this whole change report as `unknown` and `email-sync`.
export const OAUTH_PROVIDER_BY_ROUTE: ReadonlyMap<string, string> = new Map([
  [APP_PAGES.INBOX, MessagingPlatform.OUTLOOK],
  [
    `${APP_PAGES.ONBOARDING_BASE}/${OnboardingStep.EMAIL_SYNC}`,
    MessagingPlatform.OUTLOOK,
  ],
]);

// Long enough to absorb StrictMode's double-invoke and a remount racing the URL
// cleanup, short enough that a user retrying the connection is reported again.
export const OAUTH_REFUSAL_DEDUPE_WINDOW_MS = 10_000;

// Microsoft's `error` is a coarse OAuth category; the specific cause opens the
// description as `AADSTS<digits>:`. Every other provider's `error` is already the code.
export const AADSTS_CODE_PATTERN = /AADSTS\d+/;

/**
 * Only codes confirmed to reach a redirect URI. `AADSTS90094`, `AADSTS53003` and
 * `AADSTS50020` end on Microsoft's own page and never arrive; `AADSTS65001` is a
 * token-endpoint error. See the change design for the sources.
 */
export const OAUTH_REFUSAL_CODES = {
  ACCESS_DENIED: 'access_denied',
  CONSENT_NOT_GRANTED: 'AADSTS65004',
  ADMIN_POLICY_ENFORCED: 'admin_policy_enforced',
  ORG_INTERNAL: 'org_internal',
  DISALLOWED_USER_AGENT: 'disallowed_useragent',
  SERVER_ERROR: 'server_error',
  TEMPORARILY_UNAVAILABLE: 'temporarily_unavailable',
  APPLICATION_SUSPENDED: 'application_suspended',
} as const;

// Deliberately unmapped despite reaching a redirect URI: at the authorize endpoint
// `unauthorized_client` means the client application isn't permitted to request a
// code, which is as often our own registration as it is the user's tenant. Naming
// their admin sends them to someone who finds nothing, and hides our misconfiguration
// from support. The generic message with the raw code is the honest answer.

export const OAUTH_REFUSAL_I18N_KEYS = {
  TITLE: 'integration.oauthRefusal.title',
  CANCELED: 'integration.oauthRefusal.canceled',
  CONSENT_NOT_GRANTED: 'integration.oauthRefusal.consentNotGranted',
  BLOCKED_BY_ORGANIZATION: 'integration.oauthRefusal.blockedByOrganization',
  ORG_INTERNAL: 'integration.oauthRefusal.orgInternal',
  EMBEDDED_BROWSER: 'integration.oauthRefusal.embeddedBrowser',
  PROVIDER_UNAVAILABLE: 'integration.oauthRefusal.providerUnavailable',
  INTEGRATION_SUSPENDED: 'integration.oauthRefusal.integrationSuspended',
  GENERIC: 'integration.oauthRefusal.generic',
} as const;

export const OAUTH_REFUSAL_MESSAGE_KEYS: ReadonlyMap<string, string> = new Map([
  [OAUTH_REFUSAL_CODES.ACCESS_DENIED, OAUTH_REFUSAL_I18N_KEYS.CANCELED],
  // Microsoft returns AADSTS65004 for two opposite situations and the redirect params
  // cannot tell them apart: the "Back to app" return from an admin-consent request, and
  // a plain decline whose own description reads "User declined to consent to access the
  // app." So the copy must cover both — asserting the admin branch tells a user who
  // simply cancelled to wait on an approval nobody was asked for.
  [
    OAUTH_REFUSAL_CODES.CONSENT_NOT_GRANTED,
    OAUTH_REFUSAL_I18N_KEYS.CONSENT_NOT_GRANTED,
  ],
  [
    OAUTH_REFUSAL_CODES.ADMIN_POLICY_ENFORCED,
    OAUTH_REFUSAL_I18N_KEYS.BLOCKED_BY_ORGANIZATION,
  ],
  [OAUTH_REFUSAL_CODES.ORG_INTERNAL, OAUTH_REFUSAL_I18N_KEYS.ORG_INTERNAL],
  [
    OAUTH_REFUSAL_CODES.DISALLOWED_USER_AGENT,
    OAUTH_REFUSAL_I18N_KEYS.EMBEDDED_BROWSER,
  ],
  [
    OAUTH_REFUSAL_CODES.SERVER_ERROR,
    OAUTH_REFUSAL_I18N_KEYS.PROVIDER_UNAVAILABLE,
  ],
  [
    OAUTH_REFUSAL_CODES.TEMPORARILY_UNAVAILABLE,
    OAUTH_REFUSAL_I18N_KEYS.PROVIDER_UNAVAILABLE,
  ],
  [
    OAUTH_REFUSAL_CODES.APPLICATION_SUSPENDED,
    OAUTH_REFUSAL_I18N_KEYS.INTEGRATION_SUSPENDED,
  ],
]);

const ONBOARDING_LANDING_ROUTES: ReadonlyArray<string> = [
  OnboardingStep.EMAIL_SYNC,
  OnboardingStep.CONNECT,
  OnboardingStep.SLACK_SYNC,
].map((step) => `${APP_PAGES.ONBOARDING_BASE}/${step}`);

export const OAUTH_LANDING_ROUTES: ReadonlySet<string> = new Set([
  APP_PAGES.INBOX,
  APP_PAGES.DOBOX,
  ...ONBOARDING_LANDING_ROUTES,
]);

// Teams, Messenger and Instagram redirect to /integration/<platform>. Matching the
// integration route by equality drops all three.
export const OAUTH_LANDING_ROUTE_PREFIX = '/integration';

// Exported so the caller normalizes once and uses the same value for the route check,
// the provider lookup, the dedupe key and the navigate target. Normalizing only inside
// the route check let `/inbox/` pass the guard and then miss OAUTH_PROVIDER_BY_ROUTE.
export const normalizeLandingPathname = (pathname: string): string =>
  pathname.length > 1 && pathname.endsWith('/')
    ? pathname.slice(0, -1)
    : pathname;

export function isOAuthLandingRoute(pathname: string): boolean {
  const normalized = normalizeLandingPathname(pathname);

  return (
    OAUTH_LANDING_ROUTES.has(normalized) ||
    normalized === OAUTH_LANDING_ROUTE_PREFIX ||
    normalized.startsWith(`${OAUTH_LANDING_ROUTE_PREFIX}/`)
  );
}

// The refusal code reaches a first-party toast, and the whole URL is attacker-supplied.
// Provider codes are short and alphanumeric; anything else is someone else's copy.
export const OAUTH_REFUSAL_CODE_MAX_LENGTH = 32;
export const OAUTH_REFUSAL_CODE_DISALLOWED = /[^A-Za-z0-9_.-]/g;

// Ordinary user behaviour, not a fault: reported at info level so it cannot set the
// project's error rate. AADSTS65004 belongs here because a plain Outlook decline
// resolves to it, not to access_denied — leaving it out sent every Outlook
// cancellation through captureException, on the provider that motivated this change.
export const EXPECTED_OAUTH_REFUSALS: ReadonlySet<string> = new Set([
  OAUTH_REFUSAL_CODES.ACCESS_DENIED,
  OAUTH_REFUSAL_CODES.CONSENT_NOT_GRANTED,
]);

// `provider` is read from the query string, so it is attacker-supplied like the code.
// Restricting it to platforms we actually connect bounds both the text and the number
// of distinct Sentry issues a crafted link can create.
export const KNOWN_OAUTH_PROVIDERS: ReadonlySet<string> = new Set<string>([
  MessagingPlatform.GOOGLE,
  MessagingPlatform.GMAIL,
  MessagingPlatform.OUTLOOK,
  MessagingPlatform.TEAMS,
  MessagingPlatform.SLACK,
  MessagingPlatform.INSTAGRAM,
  MessagingPlatform.MESSENGER,
  MessagingPlatform.GITHUB,
]);
